import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS, stateOptions } from '../commonStateTaxData';
import {
  stepOneInitialQuestions,
  getWorksheetInitialQuestions,
  additionalAmountInitialQuestion,
  filingStatusInitialQuestion,
  getYear,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFirstNameMiddleInitial,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const stepOneForm = useRef();
  const stepOneQuestionsWithAnswers = getInitialQuestions(
    stepOneInitialQuestions,
  );
  const [stepOneQuestions, setStepOneQuestions] = useState(
    stepOneQuestionsWithAnswers,
  );

  const [lastNameDifferentOnSSN, setLastNameDifferentOnSSN] = useState(
    data.last_name_different_on_social_security_card || false,
  );

  const filingStatusForm = useRef();
  const filingStatusQuestionWithAnswer = getInitialQuestions(
    filingStatusInitialQuestion,
  );
  const [filingStatusQuestion, setFilingStatusQuestion] = useState(
    filingStatusQuestionWithAnswer,
  );

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || false,
  );
  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(
    data.exemption || '',
  );
  const [exemptionsError, setExemptionsError] = useState(false);

  const year = getYear(version);
  const [totalNoOfAllowances, setTotalNoOfAllowances] = useState(
    data.total_number_of_allowances || '',
  );
  const [totalNoOfAllowancesError, setTotalNoOfAllowancesError] = useState(false);
  const additionalAmountForm = useRef();
  const additionalAmountQuestionWithAnswer = getInitialQuestions(
    additionalAmountInitialQuestion,
  );
  const [additionalAmountQuestion, setAdditionalAmountQuestion] = useState(
    additionalAmountQuestionWithAnswer,
  );

  const [stateOfDomicile, setStateOfDomicile] = useState(
    data.state_of_domicile || '',
  );
  const [stateOfDomicileError, setStateOfDomicileError] = useState(false);

  const renderStep1Form = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={stepOneForm}
      />
      <Flex column>
        <S.QuestionWrapper mb="20px">
          <Flex>
            <label>
              <Flex>
                <S.Input
                  style={{ height: '20px' }}
                  type="checkbox"
                  value={lastNameDifferentOnSSN}
                  onChange={(e) => setLastNameDifferentOnSSN(e.target.checked)}
                  checked={lastNameDifferentOnSSN}
                  disabled={isReviewStep}
                />
                <Box ml={1} />
                Check if your last name is different on your Social Security
                card. (Line 4)
              </Flex>
            </label>
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      <FormConsumer
        questions={filingStatusQuestion}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = filingStatusQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setFilingStatusQuestion(questions);
        }}
        ref={filingStatusForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 7)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mt="10px" mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={
                  exemptionsFromWithholding === 'exemption_from_withholding_one'
                }
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>
                For tax year
                {' '}
                {year - 1}
                , I had a right to refund
                <b> all </b>
                South Carolina Income Tax withheld because I had
                <b> no </b>
                tax liability,
                <b> and </b>
                for tax year
                {' '}
                {year}
                {' '}
                I expect a refund
                of
                <b> all </b>
                South Carolina Income Tax withheld because I
                expect to have
                <b> no </b>
                tax liability.
              </Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={
                  exemptionsFromWithholding === 'exemption_from_withholding_two'
                }
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>
                For tax year 2025. I am a military servicemember or the spouse of a military
                servicemember and elect to use another state as my state of domicile (See form)
              </Text>
            </Flex>
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
      {exemptionsFromWithholding === 'exemption_from_withholding_two' && (
      <Flex mb="20px" column>
        <Flex mb="7px">
          <S.QuestionLabel
            isRequired={
                  exemptionsFromWithholding === 'exemption_from_withholding_two'
                }
          >
            State of domicile:
          </S.QuestionLabel>
        </Flex>
        <Flex>
          <S.Select
            disabled={
                  exemptionsFromWithholding
                    !== 'exemption_from_withholding_two' || isReviewStep
                }
            onChange={(e) => {
              setStateOfDomicile(e.target.value);
              setStateOfDomicileError(false);
            }}
            style={{ width: '100%' }}
            value={stateOfDomicile}
          >
            <option
              value=""
              style={{ display: 'none', color: '#666' }}
              key={0}
            >
              Select from dropdown
            </option>
            {stateOptions.map((option, idx) => (
              <option value={option.codeValue} key={idx}>
                {option.shortName}
              </option>
            ))}
          </S.Select>
        </Flex>
        {stateOfDomicileError && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </Flex>
      )}
    </Flex>
  );

  const renderAllowances = (isReviewStep) => (
    <div>
      <S.QuestionWrapper mb="20px">
        <Flex mb="7px">
          <S.QuestionLabel>Total number of allowances (Line 5)</S.QuestionLabel>
        </Flex>
        <S.Input
          type="text"
          value={totalNoOfAllowances}
          onChange={(e) => {
            setTotalNoOfAllowances(e.target.value);
            setTotalNoOfAllowancesError(false);
          }}
          disabled={isReviewStep}
        />
        {totalNoOfAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>

      <FormConsumer
        questions={additionalAmountQuestion}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmountQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmountQuestion(questions);
        }}
        ref={additionalAmountForm}
      />
    </div>
  );

  const handleValidation = () => {
    if (stepOneForm && stepOneForm.current && stepOneForm.current.validate((error) => !!error)) {
      return true;
    }

    if (filingStatusForm && filingStatusForm.current && filingStatusForm.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
      if (additionalAmountForm && additionalAmountForm.current && additionalAmountForm.current.validate((error) => !!error)) {
        return true;
      }

      if (!/^\d*$/.test(totalNoOfAllowances)) {
        setTotalNoOfAllowancesError(true);
        return true;
      }
      setTotalNoOfAllowancesError(false);
    } else {
      if (exemptionsFromWithholding === '') {
        setExemptionsError(true);
        return true;
      }
      setExemptionsError(false);
      if (
        exemptionsFromWithholding
          === 'exemption_from_withholding_two'
        && stateOfDomicile === ''
      ) {
        setStateOfDomicileError(true);
        return true;
      }
      setStateOfDomicileError(false);
    }
    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const filingStatusData = filingStatusQuestion.reduce(
      (acc, question) => ({ ...acc, ...question.answer.answer }),
      {},
    );
    let exemptionTemp = '';
    let stateOfDomicileTemp = '';
    let totalNoOfAllowancesTemp = '0';
    let additionalAmountData = resetInitialQuestions(
      additionalAmountInitialQuestion,
    ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    if (!isExemptFromWithholding) {
      totalNoOfAllowancesTemp = totalNoOfAllowances.toString();
      additionalAmountData = additionalAmountQuestion.reduce(
        (acc, question) => ({ ...acc, ...question.answer.answer }),
        {},
      );
    } else {
      exemptionTemp = exemptionsFromWithholding;
      if (exemptionTemp === 'exemption_from_withholding_two') {
        stateOfDomicileTemp = stateOfDomicile;
      }
    }
    const lastNameDifferentOnSSNData = {
      last_name_different_on_social_security_card:
        lastNameDifferentOnSSN,
    };
    const exemptionData = {
      is_exempt: isExemptFromWithholding,
      exemption: exemptionTemp,
      state_of_domicile: stateOfDomicileTemp,
    };
    const data = {
      total_number_of_allowances: totalNoOfAllowancesTemp,
      ...stepOneData,
      ...lastNameDifferentOnSSNData,
      ...filingStatusData,
      ...exemptionData,
      ...additionalAmountData,
    };
    saveFormData(data, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowances()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
